import { useMutation, useQueryClient } from 'react-query'

import { type RepoError } from '@webapp/infrastructure/interfaces/repositories/RepoError'
import { poPdfLabelEditorQueryConfig } from '@webapp/modules/purchasing/query-configs/poPdfLabelEditor'
import { pdfLabelSetRepository } from '@webapp/modules/vendor/data-access/repositories/PDFLabelSetRepository' // eslint-disable-line import/no-internal-modules
import {
  PDFLabelSet,
  type PDFLabelSetConstructor,
} from '@webapp/modules/vendor/entities/PDFLabelSet' // eslint-disable-line import/no-internal-modules
import { pdfLabelSetQueryConfig } from '@webapp/modules/vendor/query-configs/pdf-label-set' // eslint-disable-line import/no-internal-modules

export interface RenamePOPDFLabelSetParams {
  labelSet: PDFLabelSet
  name: string
}

export const useMutateRenamePOPDFLabelSet = () => {
  const queryClient = useQueryClient()

  const mutation = useMutation<
    PDFLabelSet,
    RepoError,
    RenamePOPDFLabelSetParams
  >({
    mutationFn: async ({ labelSet, name }: RenamePOPDFLabelSetParams) => {
      // Whole-translation PUT with the updated name and the re-derived key
      // (Angular Business Rule 4); the URL keeps the current stored key
      const payload: PDFLabelSetConstructor = {
        isPrimary: labelSet.isPrimary,
        key: name,
        value: {
          ...labelSet.value,
          name,
        },
      }

      // The backend canonicalizes the stored key (spaces → hyphens); use the
      // returned canonical key so downstream commands target the right set
      const { key } = await pdfLabelSetRepository.update(labelSet.key, payload)

      return new PDFLabelSet({ ...payload, key })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: pdfLabelSetQueryConfig.all().queryKey,
      })
      queryClient.invalidateQueries({
        queryKey: poPdfLabelEditorQueryConfig.initialSet().queryKey,
      })
    },
  })

  return {
    mutateAsync: mutation.mutateAsync,
    isLoading: mutation.isLoading,
  }
}
